import axios, {AxiosResponse} from 'axios';
import {
  AddEventBody,
  CreateTicketBody,
  FuroReturnsResult,
  OpenFuroReturnsBody,
  PatchTicketBody,
  TicketCountsResponse,
  TicketDetailResponse,
  TicketsListResponse,
  Ticket,
} from '../models/Ticket';
import {normalizeFuroReturnsResponse} from '../ticketsHelpers';

const API_URL = process.env.REACT_APP_TICKETS_API_URL;

// Quando há imagens, troca-se o JSON por multipart/form-data (contrato §6.1):
// `payload` = o mesmo JSON como CAMPO DE TEXTO + N partes `files`.
// O backend lê `payload` como string e faz JSON.parse; uma parte Blob/File (que o
// browser carimba com filename="blob") é roteada como arquivo e rejeitada com
// "Missing 'payload' form field". Por isso JSON.stringify puro, sem Blob.
// Sem Content-Type manual: o browser/axios definem o boundary do multipart.
export const toAttachmentForm = (payload: object, files: File[]): FormData => {
  const fd = new FormData();
  fd.append('payload', JSON.stringify(payload));
  files.forEach((f) => fd.append('files', f));
  return fd;
};

// O interceptor global do axios (AuthHelpers.ts) anexa o Bearer do usuário a toda request.
export const listTickets = (query = ''): Promise<TicketsListResponse> =>
  axios
    .get(`${API_URL}/tickets${query ? `?${query}` : ''}`)
    .then((d: AxiosResponse<TicketsListResponse>) => d.data);

export const getTicketCounts = (query = ''): Promise<TicketCountsResponse> =>
  axios
    .get(`${API_URL}/tickets/counts${query ? `?${query}` : ''}`)
    .then((d: AxiosResponse<TicketCountsResponse>) => d.data);

export const showTicket = (uid: string): Promise<TicketDetailResponse> =>
  axios
    .get(`${API_URL}/tickets/${uid}`)
    .then((d: AxiosResponse<TicketDetailResponse>) => d.data);

export const createTicket = (
  body: CreateTicketBody,
  files?: File[]
): Promise<Ticket> =>
  axios
    .post(
      `${API_URL}/tickets`,
      files && files.length ? toAttachmentForm(body, files) : body
    )
    .then((d: AxiosResponse<Ticket>) => d.data);

export const patchTicket = (
  uid: string,
  body: PatchTicketBody
): Promise<Ticket> =>
  axios
    .patch(`${API_URL}/tickets/${uid}`, body)
    .then((d: AxiosResponse<Ticket>) => d.data);

export const addTicketEvent = (
  uid: string,
  body: AddEventBody,
  files?: File[]
): Promise<TicketDetailResponse> =>
  axios
    .post(
      `${API_URL}/tickets/${uid}/events`,
      files && files.length ? toAttachmentForm(body, files) : body
    )
    .then((d: AxiosResponse<TicketDetailResponse>) => d.data);

// Abre returns diferidos para itens já no ticket (furo criado com defer_returns).
// Não dá 502: itens que falham voltam em furo_failures. A resposta é normalizada
// (ver normalizeFuroReturnsResponse) para blindar a forma exata do contrato.
export const openFuroReturns = (
  uid: string,
  body: OpenFuroReturnsBody
): Promise<FuroReturnsResult> =>
  axios
    .post(`${API_URL}/tickets/${uid}/furo-returns`, body)
    .then((d: AxiosResponse<unknown>) => normalizeFuroReturnsResponse(d.data));
